import Grid from '@mui/material/Grid';
import { WhenLoaded } from '@openx/components-core/lib/Loader/WhenLoaded.tsx';
import { type InventoryAndContentState, InventoryContentOption } from '@openx/types/targeting/inventoryAndContent.ts';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import { useTranslation } from 'react-i18next';
import type { AnyAction } from 'redux';
import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';
import { DimensionChipLine } from '../../shared/DimensionChip/DimensionChipLine.tsx';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle.tsx';
import { useInventoryAndContentLabels } from '../../shared/InventoryAndContent/constants.ts';
import {
  resetFilter,
  setComparisonOpValue,
  setContentCategoryOp,
} from '../../shared/InventoryAndContent/inventoryHelpers.ts';
import { SelectedItems } from '../../shared/SelectedItems/SelectedItems.tsx';
import { Switch } from '../../shared/Switch/Switch.tsx';
import type { TargetingItemsProps } from '../../types.ts';

export const AdunitItems = ({
  dispatchInventory = noop,
  readonly = true,
  targetingParams,
  isNotLastItem,
  switchState: chipLineSwitchState,
  showDimensionChipLine = true,
  disabledReasonMessage,
}: TargetingItemsProps<InventoryAndContentState>) => {
  const { t } = useTranslation();

  const optionPayload = targetingParams[InventoryContentOption.ADUNIT_SIZE];
  const { op: dimension, val } = optionPayload;

  const labels = useInventoryAndContentLabels();
  const label = labels[InventoryContentOption.ADUNIT_SIZE].label;
  const { usePmpSizesFetch } = useTargetingContext();

  const { isSizesLoading, sizes } = usePmpSizesFetch();

  const switchState = dimension === Intersect.NOT_INTERSECTS;
  const filtersTitle = dimension === Intersect.NOT_INTERSECTS ? t('Excluded') : t('Included');
  const itemsCount = val.length;

  return (
    <WhenLoaded loading={isSizesLoading}>
      <Grid data-test="sub-section">
        <FiltersTitle
          onClear={() =>
            dispatchInventory({
              payload: {
                contentOption: InventoryContentOption.ADUNIT_SIZE,
              },
              type: resetFilter,
            } as unknown as AnyAction)
          }
          title={`${label} [ ${itemsCount} ]`}
          readonly={readonly}
          disabledReasonMessage={disabledReasonMessage}
        />
        {!readonly && (
          <Switch
            switchState={switchState}
            onSwitchChange={() =>
              dispatchInventory({
                payload: switchState ? Intersect.INTERSECTS : Intersect.NOT_INTERSECTS,
                type: setContentCategoryOp[InventoryContentOption.ADUNIT_SIZE],
              } as unknown as AnyAction)
            }
            switchOffLabel={t('include')}
            switchOnLabel={t('exclude')}
          />
        )}

        <Grid data-test="section-items">
          <SelectedItems
            filtersTitle={filtersTitle}
            isAddIcon={dimension === Intersect.INTERSECTS}
            selectedItems={val}
            readonly={readonly}
            handleChange={values => {
              dispatchInventory({
                payload: values,
                type: setComparisonOpValue[InventoryContentOption.ADUNIT_SIZE],
              } as unknown as AnyAction);
            }}
            getItemLabel={i => sizes[i]?.name || i}
            isRemovingAll={true}
            dataTest="adunit-size"
          />
        </Grid>
        {isNotLastItem && showDimensionChipLine && <DimensionChipLine switchState={chipLineSwitchState} />}
      </Grid>
    </WhenLoaded>
  );
};
